/**
 * Aplazo availability for bulk purchases, resolved client-side by amount.
 *
 * In a bulk purchase the buyer changes the ticket count with the `+/-` selector and the
 * total is recomputed **only in the browser**: `PassengerSelectorBox` dispatches
 * `updateBulkPurchaseTotal`, which updates `purchase.total` without calling the backend.
 * The payment methods, however, come from the last `GET /purchases/{token}` and are never
 * re-evaluated, so a purchase that starts below Aplazo's minimum keeps hiding Aplazo even
 * after the buyer adds enough tickets to clear it.
 *
 * Refreshing from the backend is not an option, and both alternatives were ruled out
 * empirically:
 * - `purchaseApi.update` (PUT) does not honor `seen_price` — calling it repeatedly never
 *   changes `available_payments` nor the total.
 * - There is no preview/lightweight endpoint. Only actually creating the tickets changes
 *   what the backend considers eligible.
 *
 * So eligibility is modeled here, against the threshold the backend already publishes in
 * the whitelabel config (`env.aplazo.aplazo_min_total`), and it governs Aplazo's presence in
 * **both directions**. The withdrawal half is not symmetry for its own sake: once a bulk
 * purchase has been submitted, the backend creates the tickets and starts returning Aplazo in
 * its own lists, so lowering the count below the minimum afterwards would otherwise keep
 * offering it — the raw lists still carry it from that response.
 *
 * **Known limitation (accepted):** the backend may exclude Aplazo for reasons unrelated to
 * the amount (country, buyer context) that the client cannot observe. This module models
 * **only** the amount threshold; if the backend rejects the payment for any other reason
 * the buyer sees the regular payment error and can pick another method. Backend filters are
 * deliberately not replicated here — guessing them would drift out of sync.
 *
 * **What `total` is, and when that is exact.** After the `+/-` selector runs,
 * `updateBulkPurchaseTotal` rebuilds `purchase.total` from the departs/returns category
 * prices alone: it drops ancillaries (insurance, carbon offset) and any discount or wallet
 * balance the backend would apply. That number is also the one the pricing summary shows the
 * buyer (`PurchasePricingContainer` reads the same `total`), so comparing against it keeps
 * the Aplazo decision consistent with the price on screen — but it equals the amount the
 * backend actually charges only while no such adjustment is in play.
 *
 * Today it always is: conectagfa is the only brand with Aplazo + `aplazo_min_total` + bulk
 * enabled, and it has no insurance, no carbon offset, no discount code and no wallet, so the
 * ticket subtotal *is* the charged amount. **If any of those is ever enabled for a brand
 * with bulk + Aplazo, revisit this**: near the threshold the check could hide Aplazo when the
 * backend would have financed it, or offer it just below the minimum (the backend then
 * refuses and the buyer picks another method, as above).
 *
 * There is no better number available to compare against: the bulk flow does the seat lock,
 * the passenger save and the purchase update in a single submit, so the backend never
 * publishes a total in between. Reconstructing one here would mean reimplementing the
 * backend's pricing rules. The real fix is the `TODO` in `PassengerSelectorBox` — have the
 * selector re-sync the total with the backend — which belongs in its own change.
 */

/**
 * The `available_payments` string the backend sends for Aplazo.
 * @type {string}
 */
export const APLAZO_AVAILABLE_PAYMENT = 'aplazo';

/**
 * The `payment_methods` entry the backend sends for Aplazo.
 *
 * Both representations are required: `PaymentSelector` lists `bnpl` in
 * `requiresProviderConfig`, so without this entry it cannot resolve an icon/label and
 * renders nothing — the bare `available_payments` string is not enough.
 * @type {{type: string, provider: string, engine: string}}
 */
export const APLAZO_PAYMENT_METHOD = Object.freeze({
  type: 'bnpl',
  provider: 'aplazo',
  engine: 'aplazo',
});

/**
 * Whether a `payment_methods` entry is Aplazo.
 * @param {object} [method] - A payment method entry.
 * @returns {boolean} True when the entry is Aplazo's BNPL method.
 */
const isAplazoMethod = (method) =>
  method?.type === APLAZO_PAYMENT_METHOD.type &&
  method?.provider === APLAZO_PAYMENT_METHOD.provider;

/**
 * Resolves whether this module governs Aplazo's presence for a purchase, and how.
 *
 * Tri-state on purpose. `null` means "not ours to decide" and is the only answer that leaves
 * the backend's lists alone — it covers non-bulk purchases, brands without Aplazo enabled, an
 * unusable minimum (missing/zero/negative/non-numeric), and a total that is missing or not a
 * number. That last one matters now that a `false` withdraws Aplazo: an unknown total must
 * never be read as "below the minimum", or a transient render could hide an Aplazo the
 * backend genuinely offers.
 *
 * The minimum is normalized with `Number` because the whitelabel config delivers it as a
 * string (e.g. `"50"`).
 *
 * @param {object} params - Parameters.
 * @param {boolean} [params.bulkTicket] - Whether the purchase is a bulk/granel purchase.
 * @param {number} [params.total] - The purchase total as currently shown to the buyer.
 * @param {object} [params.aplazoConfig] - The brand's `env.aplazo` config.
 * @returns {boolean|null} `true` to offer Aplazo, `false` to withdraw it, `null` to not decide.
 */
const resolveBulkAplazoEligibility = ({ bulkTicket, total, aplazoConfig }) => {
  if (!bulkTicket) return null;
  if (!aplazoConfig?.enabled) return null;

  const minTotal = Number(aplazoConfig.aplazo_min_total);
  if (!Number.isFinite(minTotal) || minTotal <= 0) return null;

  // `Number(null)` and `Number('')` are both `0`, which would read as "below the minimum" and
  // withdraw Aplazo on a purchase whose total simply has not loaded yet. Reject the empty
  // values explicitly before coercing, so only a real number decides.
  if (total === null || total === undefined || total === '') return null;

  const currentTotal = Number(total);
  if (!Number.isFinite(currentTotal)) return null;

  return currentTotal >= minTotal;
};

/**
 * Whether Aplazo must be offered on a bulk purchase because its current total reaches the
 * configured minimum.
 *
 * Returns `false` for anything that is not a bulk purchase, for brands without Aplazo
 * enabled, and for a missing/zero/negative/non-numeric minimum — an unusable threshold
 * never means "offer Aplazo by default".
 *
 * @param {object} params - Parameters.
 * @param {boolean} [params.bulkTicket] - Whether the purchase is a bulk/granel purchase.
 * @param {number} [params.total] - The purchase total as currently shown to the buyer.
 * @param {object} [params.aplazoConfig] - The brand's `env.aplazo` config.
 * @param {boolean} [params.aplazoConfig.enabled] - Whether Aplazo is enabled for the brand.
 * @param {string|number} [params.aplazoConfig.aplazo_min_total] - Minimum total Aplazo finances.
 * @returns {boolean} True when Aplazo must be offered for this bulk total.
 */
export const shouldOfferAplazoForBulkTotal = (params) =>
  resolveBulkAplazoEligibility(params) === true;

/**
 * The payment methods to offer for a purchase, with Aplazo added or withdrawn according to
 * whether the current bulk total clears the configured minimum.
 *
 * Symmetric by design: the same client-side total that makes Aplazo appear also makes it
 * disappear. Without the withdrawal half, a purchase that was submitted once — the backend
 * creates the tickets and only then starts offering Aplazo in `available_payments` /
 * `payment_methods` — would keep showing Aplazo after the buyer lowered the count back below
 * the minimum, because the raw lists still carry it from that response.
 *
 * Both halves operate on **both** representations (see {@link APLAZO_PAYMENT_METHOD}), never
 * duplicating what the backend already offers, and append so existing methods keep their
 * order. Nothing but Aplazo is ever touched.
 *
 * When this module does not govern the purchase (non-bulk, no Aplazo config, unusable
 * minimum, unknown total — see `resolveBulkAplazoEligibility`) it returns the **same array
 * references** it received, so that path is a verifiable no-op that introduces no new
 * identities downstream. The same holds whenever there is simply nothing to add or remove.
 *
 * This is intentionally derived at render time rather than written to the store: mutating
 * the backend's response in shared state would reach every consumer, including the payment
 * flow, instead of just the selector UI.
 *
 * @param {object} params - Parameters.
 * @param {boolean} [params.bulkTicket] - Whether the purchase is a bulk/granel purchase.
 * @param {number} [params.total] - The purchase total as currently shown to the buyer.
 * @param {object} [params.aplazoConfig] - The brand's `env.aplazo` config.
 * @param {string[]} [params.availablePayments] - The purchase's legacy `available_payments` list.
 * @param {object[]} [params.paymentMethods] - The purchase's `payment_methods` list.
 * @returns {{availablePayments: string[], paymentMethods: object[]}} The lists to render.
 */
export const getBulkAvailablePayments = ({
  bulkTicket,
  total,
  aplazoConfig,
  availablePayments = [],
  paymentMethods = [],
}) => {
  const qualifies = resolveBulkAplazoEligibility({ bulkTicket, total, aplazoConfig });

  if (qualifies === null) {
    return { availablePayments, paymentMethods };
  }

  if (qualifies) {
    return {
      availablePayments: availablePayments.includes(APLAZO_AVAILABLE_PAYMENT)
        ? availablePayments
        : [...availablePayments, APLAZO_AVAILABLE_PAYMENT],
      paymentMethods: paymentMethods.some(isAplazoMethod)
        ? paymentMethods
        : [...paymentMethods, APLAZO_PAYMENT_METHOD],
    };
  }

  return {
    availablePayments: availablePayments.includes(APLAZO_AVAILABLE_PAYMENT)
      ? availablePayments.filter((payment) => payment !== APLAZO_AVAILABLE_PAYMENT)
      : availablePayments,
    paymentMethods: paymentMethods.some(isAplazoMethod)
      ? paymentMethods.filter((method) => !isAplazoMethod(method))
      : paymentMethods,
  };
};
